import { DynamicField, TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import {
  BatchCreateBalancesPaymentRequestsRequest,
  GetPatientsBalanceResponse_PatientBalance,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { TagType } from '@frontend/design-system';

const formatKey = (key: string) => '{{' + key + '}}';
export const mappedTags = (templateTags: DynamicField[]) =>
  templateTags.map((tag) => {
    const transformedValue = tag.transformations?.find(({ arg }) => !!arg)?.arg;
    const formattedTag: TagType = {
      label: tag.label ?? '',
      value: (transformedValue || tag.exampleValues?.join(' ')) ?? '',
      key: tag.key ? formatKey(tag.key) : '',
      optional: tag.isRequired ? false : true,
    };
    return formattedTag;
  });

export const transformPatientsData = (patients: GetPatientsBalanceResponse_PatientBalance[]) => {
  const result: BatchCreateBalancesPaymentRequestsRequest['requests'] = {};

  patients.forEach(
    ({
      patientId,
      balance,
      balanceType,
      locationIds,
      provider,
      pmsName,
      phoneNumber,
      firstName,
      lastName,
      preferredName,
    }) => {
      locationIds?.forEach((locationId) => {
        if (!result[locationId]) {
          result[locationId] = { requests: [] };
        }

        result[locationId].requests?.push({
          patientId,
          amount: balance,
          provider,
          locationId,
          balanceType,
          pmsName,
          phoneNumber: !!phoneNumber ? phoneNumber : undefined,
          firstName,
          lastName,
          preferredName,
        });
      });
    }
  );

  return result;
};

export const derivePatientGroups = (patientBalances: GetPatientsBalanceResponse_PatientBalance[]) => {
  const validUsers = patientBalances.filter((user) => user.locationIds?.length === 1);
  const [usersWithPhone, usersWithoutPhone] = validUsers.reduce(
    (acc, user) => {
      acc[user.phoneNumber ? 0 : 1].push(user);
      return acc;
    },
    [[], []] as [GetPatientsBalanceResponse_PatientBalance[], GetPatientsBalanceResponse_PatientBalance[]]
  );
  const uniqueLocations = [...new Set(usersWithPhone.flatMap((col) => col.locationIds))] as string[];

  return {
    patients: usersWithPhone,
    patientsUniqueLocations: uniqueLocations,
    patientsWithoutPhone: usersWithoutPhone,
  };
};

export const getAllPatientsCount = (patientBalances: GetPatientsBalanceResponse_PatientBalance[] | undefined): number =>
  patientBalances?.filter((pb) => !!pb.phoneNumber && pb.locationIds?.length === 1).length ?? 0;

type BuildBulkCreatePayloadParams = {
  patients: GetPatientsBalanceResponse_PatientBalance[];
  internalSelected: Record<string, boolean>;
  allPatientsCount: number;
  patientsUniqueLocations: string[];
  useTTP?: boolean;
  smsBody?: string;
};

export const buildBulkCreatePayload = ({
  patients,
  internalSelected,
  allPatientsCount,
  patientsUniqueLocations,
  useTTP,
  smsBody,
}: BuildBulkCreatePayloadParams): BatchCreateBalancesPaymentRequestsRequest => {
  const newlySelectedPatients = patients.filter((patient) => internalSelected[patient.patientId]);
  const isAllSelected = newlySelectedPatients.length === allPatientsCount;

  const payload: BatchCreateBalancesPaymentRequestsRequest = {
    requests: !isAllSelected
      ? transformPatientsData(newlySelectedPatients)
      : patientsUniqueLocations.reduce((o, k) => ({ ...o, [k]: {} }), {}),
    createRequestsForAll: isAllSelected,
    onlyIncludeNewBalances: false,
    actionType: useTTP ? 'CREATE_AND_TTP' : 'CREATE',
  };

  if (useTTP) {
    payload.template = {
      templateString: smsBody,
      templateTypeSlug: TemplateType_Slug.MANUAL_PAYMENT_REQUEST,
    };
  }

  return payload;
};
